import React, { useState, useEffect } from 'react';

const Login = () => {
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'dark';
  });

  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    // Testimonial rotation
    const testimonialInterval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    // Feature rotation - modified for infinite loop
    const featureInterval = setInterval(() => {
      setCurrentFeatureIndex((prev) => {
        const next = prev + 1;
        // When we reach the end of the first set, reset to beginning without animation
        if (next >= 6) {
          const track = document.querySelector('.features-track');
          track.setAttribute('data-resetting', 'true');
          setTimeout(() => {
            setCurrentFeatureIndex(0);
            track.removeAttribute('data-resetting');
          }, 0);
          return prev;
        }
        return next;
      });
    }, 4000);

    return () => {
      clearInterval(testimonialInterval);
      clearInterval(featureInterval);
    };
  }, []);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleLogin = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/login`;
  };

  const testimonials = [
    {
      text: "This tool completely changed how I understand my music taste. The insights are incredibly accurate!",
      author: "Sarah",
      role: "Music Enthusiast",
      image: "https://i.pravatar.cc/150?img=1"
    },
    {
      text: "The visualizations are beautiful and the real-time updates keep me engaged with my music journey.",
      author: "Mike",
      role: "DJ",
      image: "https://i.pravatar.cc/150?img=2"
    },
    {
      text: "Finally, a tool that helps me discover new music based on my actual listening patterns.",
      author: "Alex",
      role: "Playlist Curator",
      image: "https://i.pravatar.cc/150?img=3"
    },
    {
      text: "I love how it shows my music evolution over time. It's like having a personal music diary!",
      author: "Emma",
      role: "Music Blogger",
      image: "https://i.pravatar.cc/150?img=4"
    }
  ];

  const faqItems = [
    {
      question: "Is my data secure?",
      answer: "Yes, we use industry-standard encryption and only access the data you explicitly allow. Your privacy is our top priority.",
      icon: "🔒"
    },
    {
      question: "What data do you collect?",
      answer: "We only collect your listening history and preferences to provide personalized analytics. We never store sensitive information.",
      icon: "📊"
    },
    {
      question: "Can I disconnect my account?",
      answer: "Yes, you can revoke access at any time through your Spotify account settings or directly from our platform.",
      icon: "🔌"
    },
    {
      question: "How often is the data updated?",
      answer: "Your listening data is updated in real-time as you use Spotify. Analytics are refreshed every 24 hours.",
      icon: "🔄"
    },
    {
      question: "Is this free to use?",
      answer: "Yes, all our features are completely free to use. We're passionate about helping you discover your music taste.",
      icon: "🎵"
    },
    {
      question: "What makes this different from Spotify Wrapped?",
      answer: "Unlike Spotify Wrapped, we provide real-time insights, year-round access, and more detailed analytics about your listening habits.",
      icon: "✨"
    }
  ];

  return (
    <div className="login-container">
      <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
        {theme === 'dark' ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>
      <div className="hero-section">
        <div className="hero-content">
          <div className="logo">
            <svg viewBox="0 0 24 24" fill={theme === 'dark' ? 'currentColor' : '#FF69B4'}>
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
          </div>
          <h1>Your Music Journey, <br />Visualized</h1>
          <p>Get real-time insights into your listening habits with our advanced Spotify analytics. Discover your musical identity through detailed statistics and beautiful visualizations.</p>
          <div className="beta-notice">
            <p>🚀 Currently in beta - Spotify login is limited to invited users while we fine-tune the experience. Stay tuned!</p>
          </div>
          <button className="login-button" onClick={handleLogin}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
            Connect with Spotify
          </button>
        </div>
        <div className="hero-features">
          {/* Features Section */}
          <div className="features-section">
            <div className="features-slider">
              <div 
                className="features-track" 
                style={{ 
                  transform: `translateX(calc(-${currentFeatureIndex * (window.innerWidth <= 768 ? 100 : 100/6)}%))`,
                  transition: currentFeatureIndex === 0 ? 'none' : 'transform 0.5s ease-in-out'
                }}
              >
                {/* First set of features */}
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Real-time Analytics</h3>
                  <p>Track your listening habits as they evolve with instant updates and insights.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Live Updates</span>
                    <span className="tag">Instant Insights</span>
                    <span className="tag">Detailed Stats</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20V10m-6 10V4m12 16v-6M4 20h16" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Top Charts</h3>
                  <p>Discover your most played artists and tracks with detailed statistics.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Top Artists</span>
                    <span className="tag">Favorite Songs</span>
                    <span className="tag">Weekly Rankings</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Genre Analysis</h3>
                  <p>Explore your music taste across different genres and time periods.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Genre Breakdown</span>
                    <span className="tag">Time Analysis</span>
                    <span className="tag">Taste Evolution</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Mood Tracking</h3>
                  <p>Understand how your music choices reflect your emotional state over time.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Emotion Analysis</span>
                    <span className="tag">Mood Patterns</span>
                    <span className="tag">Daily Insights</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 7h-6l-1-7z" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 11v3m0 0v3m0-3h3m-3 0H9" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Discover Weekly</h3>
                  <p>Get personalized music recommendations based on your listening patterns.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Custom Playlists</span>
                    <span className="tag">New Discoveries</span>
                    <span className="tag">Weekly Updates</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Year in Review</h3>
                  <p>Get a comprehensive summary of your music journey throughout the year.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Annual Stats</span>
                    <span className="tag">Growth Tracking</span>
                    <span className="tag">Yearly Insights</span>
                  </div>
                </div>
                {/* Duplicate set of features for infinite loop */}
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Real-time Analytics</h3>
                  <p>Track your listening habits as they evolve with instant updates and insights.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Live Updates</span>
                    <span className="tag">Instant Insights</span>
                    <span className="tag">Detailed Stats</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20V10m-6 10V4m12 16v-6M4 20h16" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Top Charts</h3>
                  <p>Discover your most played artists and tracks with detailed statistics.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Top Artists</span>
                    <span className="tag">Favorite Songs</span>
                    <span className="tag">Weekly Rankings</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Genre Analysis</h3>
                  <p>Explore your music taste across different genres and time periods.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Genre Breakdown</span>
                    <span className="tag">Time Analysis</span>
                    <span className="tag">Taste Evolution</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Mood Tracking</h3>
                  <p>Understand how your music choices reflect your emotional state over time.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Emotion Analysis</span>
                    <span className="tag">Mood Patterns</span>
                    <span className="tag">Daily Insights</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 7h-6l-1-7z" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 11v3m0 0v3m0-3h3m-3 0H9" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Discover Weekly</h3>
                  <p>Get personalized music recommendations based on your listening patterns.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Custom Playlists</span>
                    <span className="tag">New Discoveries</span>
                    <span className="tag">Weekly Updates</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Year in Review</h3>
                  <p>Get a comprehensive summary of your music journey throughout the year.</p>
                  <div className="feature-hover-content">
                    <span className="tag">Annual Stats</span>
                    <span className="tag">Growth Tracking</span>
                    <span className="tag">Yearly Insights</span>
                  </div>
                </div>
              </div>
              <div className="features-dots">
                {[...Array(6)].map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${index === currentFeatureIndex ? 'active' : ''}`}
                    onClick={() => setCurrentFeatureIndex(index)}
                    aria-label={`Go to feature ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Spinning Testimonials */}
        <div className="testimonials-section">
          <div className="testimonial-container">
            <div className="testimonial-content">
              <img 
                src={testimonials[currentTestimonial].image} 
                alt={testimonials[currentTestimonial].author}
                className="testimonial-image"
              />
              <div className="testimonial-text">
                <p>"{testimonials[currentTestimonial].text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonials[currentTestimonial].author}</strong>
                  <span>{testimonials[currentTestimonial].role}</span>
                </div>
              </div>
            </div>
            <div className="testimonial-dots">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`dot ${index === currentTestimonial ? 'active' : ''}`}
                  onClick={() => setCurrentTestimonial(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqItems.map((faq, index) => (
              <div 
                key={index}
                className={`faq-item ${openFaqIndex === index ? 'open' : ''}`}
                onClick={() => toggleFaq(index)}
              >
                <div className="faq-header">
                  <span className="faq-icon">{faq.icon}</span>
                  <h3>{faq.question}</h3>
                  <button 
                    className="faq-toggle"
                    aria-label={openFaqIndex === index ? "Close answer" : "Show answer"}
                  >
                    <svg 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      className={openFaqIndex === index ? 'rotate' : ''}
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>
                <div className={`faq-content ${openFaqIndex === index ? 'show' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <footer className="hero-footer">
          <p>Made with ♥️ by <a href="https://github.com/erxcc" target="_blank" rel="noopener noreferrer">Eric</a></p>
        </footer>
      </div>
    </div>
  );
};

export default Login; 